// KPI card — large number, optional delta vs last month.
// Pure presentation. Receives label, value (formatted string), and trend info.
import React from 'react';
import { formatDelta, formatDeltaPp } from '../lib/format.js';
import { useI18n } from '../i18n/index.jsx';

export default function KpiCard({ label, value, delta, accent = 'slate', icon, compareLabel, deltaIsPp = false }) {
  const { t } = useI18n();
  const cmp = compareLabel || t('dashboard.vsLast');
  const accentClasses = {
    slate:    'text-slate-900 dark:text-slate-100',
    income:   'text-emerald-600 dark:text-emerald-400',
    expense:  'text-red-600 dark:text-red-400',
    net:      'text-brand-600 dark:text-brand-400',
    // Phase 50: networth — uses brand color when positive, red when negative.
    // Value-only (no delta) — it's cumulative balance, not period compare.
    networth: (value >= 0
      ? 'text-brand-600 dark:text-brand-400'
      : 'text-red-600 dark:text-red-400'),
  };
  const deltaColors = {
    income:  { up: 'text-emerald-600 dark:text-emerald-400', down: 'text-red-600 dark:text-red-400' },
    expense: { up: 'text-red-600 dark:text-red-400',         down: 'text-emerald-600 dark:text-emerald-400' },
    net:      { up: 'text-emerald-600 dark:text-emerald-400', down: 'text-red-600 dark:text-red-400' },
    networth: { up: 'text-emerald-600 dark:text-emerald-400', down: 'text-red-600 dark:text-red-400' },
    slate:    { up: 'text-slate-600 dark:text-slate-400',     down: 'text-slate-600 dark:text-slate-400' },
  };
  // Phase 50: for 'networth' accent, hide the period-comparison line
  // (the value is cumulative balance, not a monthly compare). The
  // card shows just label + value + an optional helper line (set
  // externally via subtitle prop).
  const showDelta = accent !== 'networth' &&
    delta !== undefined && delta !== null && Number.isFinite(delta);
  const deltaCls = showDelta && delta !== 0
    ? (delta > 0 ? deltaColors[accent].up : deltaColors[accent].down)
    : 'text-slate-400 dark:text-slate-500';

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-3 sm:p-4">
      <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mb-1">
        {icon && <span aria-hidden="true">{icon}</span>}
        <span>{label}</span>
      </div>
      <div className={`text-xl sm:text-2xl font-bold tabular-nums truncate ${accentClasses[accent]}`}>
        {value}
      </div>
      {showDelta ? (
        // Phase 77g: when deltaIsPp=true, render percentage-point delta
        // (e.g. savings rate). Otherwise the standard % delta.
        <div className={`text-xs mt-1 ${deltaCls}`}>
          {cmp} {deltaIsPp ? formatDeltaPp(delta) : formatDelta(delta)}
        </div>
      ) : (
        // Phase 49l: ghost text instead of bare "—" so the line reads as
        // "no comparison available" rather than a broken display.
        <div className="text-xs mt-1 text-slate-300 dark:text-slate-600">
          {t('kpi.noComparison')}
        </div>
      )}
    </div>
  );
}
